import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { withOverlayControls } from '@eb/eds-structure';
import { trackEventFromState } from '@eb/site-analytics';

import {
    getUserCurrentLocation as getUserCurrentLocationAction,
    searchLocationSuggestions as searchLocationSuggestionsAction,
} from '../../../../redux/actions/location';
import {
    handleApplyFilter as handleApplyFilterAction,
    handleRemoveFilter as handleRemoveFilterAction,
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsAction,
    searchByBBoxOrPlace as searchByBBoxOrPlaceAction,
    handleRemoveOnlineFilter as handleRemoveOnlineFilterAction,
    searchSuggestionsForCity as searchSuggestionsForCityAction,
} from '../../../../redux/actions/search';
import { openCloseAnimatedDrawer } from '../../../../redux/actions/ui';

import {
    getSelectedCategory,
    getSelectedFormat,
    getSelectedDate,
    getSearchHeaderTitle,
} from '../../../../redux/selectors/search';

import { SearchInput } from './components';

import { FOLLOWED_ORGS_FILTER_VALUE } from '../../../../constants/filters';
import {
    LOCATION_DIALOG_CONTENT,
    SUGGESTIONS_PROPTYPE,
} from '../../../../constants';

const MobileFilterHeader = loadable(
    () => import('../../components/MobileFilterHeader'),
    {
        resolveComponent: (components) => components.MobileFilterHeader,
    },
);
const AdminSearchSubHeaderContainer = loadable(
    () => import('../AdminSearchSubHeader'),
    {
        resolveComponent: (components) =>
            components.AdminSearchSubHeaderContainer,
    },
);

import './SearchHeaderContainer.scss';

export class SearchHeader extends PureComponent {
    static propTypes = {
        /*
         * From withOverlayControls HoC
         */
        showOverlay: PropTypes.func.isRequired,
        hideOverlay: PropTypes.func,
        addOverlayNotification: PropTypes.func,
        hideOverlayNotification: PropTypes.func,
        trackGAEvent: PropTypes.func,
        handleApplyFilter: PropTypes.func,
        handleRemoveFilter: PropTypes.func,
        searchHeaderTitle: PropTypes.string,
        selectedCategory: PropTypes.string,
        selectedPrice: PropTypes.string,
        selectedDate: PropTypes.string,
        selectedFormat: PropTypes.string,
        isBotRequest: PropTypes.bool,
        currentPlace: PropTypes.string,
        term: PropTypes.string,
        locationSlug: PropTypes.string,
        locationDenied: PropTypes.bool,
        suggestions: SUGGESTIONS_PROPTYPE,
        handleUseCurrentLocation: PropTypes.func,
        handleSearchSuggestionChange: PropTypes.func,
        handleLocationSuggestionChange: PropTypes.func,
        handleLocationSelect: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
        isLoading: PropTypes.bool,
        facets: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                count: PropTypes.number,
                displayName: PropTypes.string,
            }),
        ),
        /**
         * Callback to execute when a filter is selected
         */
        openCloseAnimatedDrawer: PropTypes.func,
        /**
         * Type of individual filter active in the filter list
         */
        filterTypeToShow: PropTypes.string,
    };

    componentDidUpdate(prevProps) {
        const { locationDenied, showOverlay } = this.props;

        if (locationDenied !== prevProps.locationDenied) {
            showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }
    }

    render() {
        const {
            searchHeaderTitle,
            selectedCategory,
            selectedPrice,
            selectedDate,
            handleRemoveFilter,
            isBotRequest,
            locationSlug,
            currentPlace,
            term,
            handleSearchSuggestionChange,
            handleLocationSuggestionChange,
            handleApplyFilter,
            selectedFormat,
            suggestions,
            facets,
            isLoading,
            trackGAEvent,
            handleLocationSelect,
            onGoogleMapsLoaded,
            location,
            history,
            openCloseAnimatedDrawer,
            filterTypeToShow,
            selectedCurrency,
            selectedLanguage,
            followedOrganizers,
            selectedOrganizations,
            selectedHash,
            handleUseCurrentLocation,
            isOnline,
            handleApplyOnlineFilter,
            handleRemoveOnlineFilter,
            searchSuggestionsForCityAction,
        } = this.props;

        let headerContent = (
            <SearchInput
                searchSuggestionsForCityAction={searchSuggestionsForCityAction}
                hasHistory={true}
                handleSearchSuggestionChange={handleSearchSuggestionChange}
                handleLocationSuggestionChange={handleLocationSuggestionChange}
                handleUseCurrentLocation={handleUseCurrentLocation}
                handleApplyFilter={handleApplyFilter}
                initialSearchQuery={term}
                currentPlace={currentPlace}
                suggestions={suggestions}
                trackGAEvent={trackGAEvent}
                handleLocationSelect={handleLocationSelect}
                onGoogleMapsLoaded={onGoogleMapsLoaded}
                location={location}
                history={history}
            />
        );

        // for bots we will just show the title
        if (isBotRequest) {
            headerContent = (
                <h1 className="eds-text-hm eds-text-color--grey-700 search-header__bot-friendly">
                    {searchHeaderTitle}
                </h1>
            );
        }

        return (
            <header className="search-header">
                {headerContent}
                {
                    <MobileFilterHeader
                        followedOrganizers={followedOrganizers}
                        selectedOrganizations={selectedOrganizations}
                        selectedHash={selectedHash}
                        selectedCurrency={selectedCurrency}
                        selectedLanguage={selectedLanguage}
                        selectedDate={selectedDate}
                        selectedCategory={selectedCategory}
                        selectedPrice={selectedPrice}
                        selectedFormat={selectedFormat}
                        handleRemoveFilter={handleRemoveFilter}
                        isBotRequest={isBotRequest}
                        locationSlug={locationSlug}
                        isOnline={isOnline}
                        facets={facets}
                        isLoading={isLoading}
                        trackGAEvent={trackGAEvent.bind(this)}
                        history={history}
                        openCloseAnimatedDrawer={openCloseAnimatedDrawer}
                        filterTypeToShow={filterTypeToShow}
                        handleApplyOnlineFilter={handleApplyOnlineFilter}
                        handleRemoveOnlineFilter={handleRemoveOnlineFilter}
                    />
                }
                {history.location.hash === '#debug' && (
                    <AdminSearchSubHeaderContainer />
                )}
            </header>
        );
    }
}

const _mapStateToProps = (state) => ({
    selectedPrice: state.search.eventSearch.price,
    selectedCategory: getSelectedCategory(state.search.eventSearch.tags),
    selectedFormat: getSelectedFormat(state.search.eventSearch.tags),
    selectedDate: getSelectedDate(
        state.search.eventSearch.dates,
        state.search.eventSearch.dateRange,
    ),
    selectedCurrency:
        state.search.eventSearch.currencies.length > 0
            ? state.search.eventSearch.currencies[0]
            : '',
    selectedLanguage:
        state.search.eventSearch.languages.length > 0
            ? state.search.eventSearch.languages
            : '',
    followedOrganizers:
        state.search.eventSearch.special.indexOf(FOLLOWED_ORGS_FILTER_VALUE) >
        -1,
    selectedOrganizations:
        state.search.eventSearch.organizationsOr.length > 0
            ? state.search.eventSearch.organizationsOr
            : false,
    selectedHash: state.search.eventSearch.hash,
    searchHeaderTitle: getSearchHeaderTitle(state),
    isBotRequest: state.app.isBotRequest,
    locationSlug: state.location.slug,
    currentPlace: state.location.currentPlace,
    isOnline: state.search.eventSearch.onlineEventsOnly,
    term: state.search.eventSearch.q,
    locationDenied: state.location.locationDenied,
    currentLocationCoordinates: state.location.currentLocationCoordinates,
    suggestions: state.suggestions,
    isLoading: state.pagination.loading,
    trackGAEvent: (action) => trackEventFromState(state, action),
    location: state.location,
    filterTypeToShow: state.ui.filterTypeToShow,
});

const _mapDispatchToProps = (dispatch, ownProps) => ({
    handleApplyFilter: (...args) =>
        dispatch(handleApplyFilterAction(ownProps.history, ...args)),
    handleRemoveFilter: (...args) =>
        dispatch(handleRemoveFilterAction(ownProps.history, ...args)),

    handleUseCurrentLocation: (...args) =>
        dispatch(getUserCurrentLocationAction(ownProps.history, ...args)),

    handleLocationSuggestionChange: (...args) =>
        dispatch(searchLocationSuggestionsAction(ownProps.history, ...args)),
    handleSearchSuggestionChange: (...args) =>
        dispatch(searchAutocompleteSuggestionsAction(...args)),
    handleLocationSelect: (...args) =>
        dispatch(searchByBBoxOrPlaceAction(ownProps.history, ...args)),
    openCloseAnimatedDrawer: (...args) =>
        dispatch(openCloseAnimatedDrawer(...args)),
    handleApplyOnlineFilter: (...args) =>
        dispatch(searchByBBoxOrPlaceAction(ownProps.history, ...args)),
    handleRemoveOnlineFilter: (...args) =>
        dispatch(handleRemoveOnlineFilterAction(ownProps.history, ...args)),
    searchSuggestionsForCityAction: (...args) =>
        dispatch(searchSuggestionsForCityAction(...args)),
});

export const SearchHeaderContainer = withRouter(
    withOverlayControls(
        connect(_mapStateToProps, _mapDispatchToProps)(SearchHeader),
    ),
);
